import { useQuery, useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { Download, FileSignature, Printer } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { AGREEMENT_LABEL, noticeLabel, validateSignature, WTR_STATE_LABEL, type Agreement, type EmployeeHistoryEntry, type SignatureInput, type WtrState, type WtrStatus } from '@tradeoutlet/shared';
import { Badge, Button, Card, ConfirmDialog, Field, Input, Modal, Spinner, Textarea } from '@/components/ui';
import { api, errorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { fmt } from '@/lib/format';
import { useMe, useWorkspaceId } from '@/lib/hooks';
import { useUi } from '@/lib/store';
import { HistoryList } from './PositionsTab';

/** Feedback 1 #2: the 48-hour opt-out is a signed agreement, never an admin toggle. */
export const londonToday = () => DateTime.now().setZone('Europe/London').toISODate()!;

const STATE_TONE: Record<WtrState, string> = {
  none: 'bg-slate-100 text-slate-700', opted_out: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200', legacy: 'bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-200',
  withdrawing: 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200', withdrawn: 'bg-slate-100 text-slate-700',
};
export function WtrBadge({ state }: { state: WtrState }) {
  return <Badge className={STATE_TONE[state]}><span data-testid="wtr-state">{WTR_STATE_LABEL[state]}</span></Badge>;
}

/** One sentence per agreement event for the profile history. */
export function describeWtrEvent(e: Pick<EmployeeHistoryEntry, 'action' | 'metadata' | 'actor_name'>): string {
  const m = e.metadata as { method?: string; notice_days?: number; effective_on?: string; legacy?: boolean; kind?: string };
  switch (e.action) {
    case 'workforce.wtr.requested': return `Signature requested by ${e.actor_name ?? 'someone'}${m.legacy ? ' (to replace the legacy record)' : ''}`;
    case 'workforce.wtr.signed': return `Signed the opt-out (${m.method === 'drawn' ? 'drawn' : 'typed'} signature${m.notice_days ? `, ${noticeLabel(m.notice_days)} notice to withdraw` : ''})`;
    case 'workforce.wtr.withdrawal_signed': return `Signed a withdrawal: the opt-out ends ${fmt.date(m.effective_on)}`;
    case 'workforce.wtr.cancelled': return `Request cancelled by ${e.actor_name ?? 'someone'}`;
    case 'workforce.wtr.declined': return 'Declined the request';
    default: return e.action;
  }
}

/** Canvas signature pad: pointer + touch, emits a PNG data URL (null when cleared). */
export function SignaturePad({ onChange }: { onChange: (png: string | null) => void }) {
  const ref = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const [empty, setEmpty] = useState(true);
  const pos = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    return [((e.clientX - r.left) * e.currentTarget.width) / r.width, ((e.clientY - r.top) * e.currentTarget.height) / r.height] as const;
  };
  const ctx = () => {
    const c = ref.current?.getContext('2d');
    if (c) { c.lineWidth = 2.5; c.lineCap = 'round'; c.lineJoin = 'round'; c.strokeStyle = '#111'; }
    return c;
  };
  return (
    <div>
      <canvas ref={ref} width={560} height={160} aria-label="Signature pad: draw your signature" role="img" data-testid="signature-pad"
        className="h-40 w-full touch-none rounded-md border border-dashed border-slate-400 bg-white"
        onPointerDown={e => { const c = ctx(); if (!c) return; e.currentTarget.setPointerCapture(e.pointerId); drawing.current = true; const [x, y] = pos(e); c.beginPath(); c.moveTo(x, y); }}
        onPointerMove={e => { if (!drawing.current) return; const c = ctx(); if (!c) return; const [x, y] = pos(e); c.lineTo(x, y); c.stroke(); }}
        onPointerUp={() => { if (!drawing.current) return; drawing.current = false; setEmpty(false); onChange(ref.current!.toDataURL('image/png')); }} />
      <div className="mt-1 flex justify-between text-xs text-slate-500">
        <span>{empty ? 'Sign with your finger, pen or mouse' : 'Signature captured'}</span>
        <button type="button" className="underline" onClick={() => { ctx()?.clearRect(0, 0, 560, 160); setEmpty(true); onChange(null); }}>Clear</button>
      </div>
    </div>
  );
}

/** Typed-name or drawn signature, plus today's date. */
export function SignatureForm({ signerName, busy, submitLabel, onSubmit, onCancel }: { signerName: string; busy?: boolean; submitLabel: string; onSubmit: (s: SignatureInput) => void; onCancel: () => void }) {
  const [method, setMethod] = useState<'typed' | 'drawn'>('typed');
  const [typed, setTyped] = useState('');
  const [png, setPng] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);
  const date = londonToday();
  const input: SignatureInput = method === 'typed' ? { method, typed_name: typed, date } : { method, drawn_png: png ?? undefined, date };
  const error = validateSignature(input, signerName);
  return (
    <form className="space-y-3" onSubmit={e => { e.preventDefault(); setTouched(true); if (!error) onSubmit(input); }}>
      <fieldset className="flex gap-4 text-sm">
        <legend className="sr-only">How do you want to sign?</legend>
        <label className="flex items-center gap-1"><input type="radio" name="sig" checked={method === 'typed'} onChange={() => setMethod('typed')} />Type my name</label>
        <label className="flex items-center gap-1"><input type="radio" name="sig" checked={method === 'drawn'} onChange={() => setMethod('drawn')} />Draw my signature</label>
      </fieldset>
      {method === 'typed'
        ? <Field label="Your full name" hint={`Type it as: ${signerName}`}><Input value={typed} onChange={e => setTyped(e.target.value)} autoComplete="name" data-testid="typed-name" /></Field>
        : <SignaturePad onChange={setPng} />}
      <p className="text-sm">Date: <span className="font-medium">{fmt.date(date)}</span></p>
      {touched && error && <p role="alert" className="text-sm text-red-700">{error}</p>}
      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" onClick={onCancel}>Cancel</Button>
        <Button type="submit" loading={busy} data-testid="sign-submit"><FileSignature className="h-4 w-4" />{submitLabel}</Button>
      </div>
    </form>
  );
}

const fileName = (a: Agreement) => `${a.kind === 'wtr_opt_out' ? '48-hour-opt-out' : '48-hour-opt-out-withdrawal'}-${(a.signed_name ?? 'employee').replace(/[^\w-]+/g, '-')}-${a.signed_on}.html`;

async function download(ws: string, a: Agreement) {
  const blob = await api.wf.agreementDocument(ws, a.id, true);
  const url = URL.createObjectURL(blob);
  const link = Object.assign(document.createElement('a'), { href: url, download: fileName(a) });
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** The frozen signed copy in a sandboxed frame (no scripts), with print (to PDF) and download. */
export function SignedCopyModal({ agreement, onClose }: { agreement: Agreement; onClose: () => void }) {
  const ws = useWorkspaceId();
  const frame = useRef<HTMLIFrameElement>(null);
  const doc = useQuery({ queryKey: ['wf', ws, 'agreement-doc', agreement.id], queryFn: async () => (await api.wf.agreementDocument(ws!, agreement.id)).text(), enabled: Boolean(ws) });
  return (
    <Modal open onClose={onClose} wide title={`${AGREEMENT_LABEL[agreement.kind]}: signed copy`}
      footer={<><Button variant="ghost" onClick={() => frame.current?.contentWindow?.print()}><Printer className="h-4 w-4" />Print or save as PDF</Button><Button onClick={() => void download(ws!, agreement)}><Download className="h-4 w-4" />Download</Button></>}>
      {doc.data ? <iframe ref={frame} title="Signed agreement" sandbox="allow-same-origin allow-modals" srcDoc={doc.data} className="h-[60vh] w-full rounded border border-slate-200 bg-white" data-testid="signed-copy" />
        : doc.error ? <p role="alert">{errorMessage(doc.error)}</p> : <Spinner />}
    </Modal>
  );
}

/** The employee reads and signs a waiting request (or declines it). */
export function SignAgreementModal({ id, onClose, onDone }: { id: string; onClose: () => void; onDone?: (s: WtrStatus) => void }) {
  const ws = useWorkspaceId();
  const toast = useUi(s => s.toast);
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [decline, setDecline] = useState(false);
  const q = useQuery({ queryKey: ['wf', ws, 'agreement', id], queryFn: () => api.wf.agreement(ws!, id), enabled: Boolean(ws) });
  const done = (s?: WtrStatus) => { void qc.invalidateQueries({ queryKey: ['wf', ws] }); if (s) onDone?.(s); onClose(); };
  const p = q.data;
  return (
    <Modal open onClose={onClose} wide title={p?.title ?? AGREEMENT_LABEL.wtr_opt_out}>
      {!p ? (q.error ? <p role="alert">{errorMessage(q.error)}</p> : <Spinner />) : p.agreement.status !== 'pending' ? <p>This request is {p.agreement.status}.</p> : (
        <div className="space-y-4">
          {p.agreement.message && <p className="rounded-md bg-slate-50 p-3 text-sm dark:bg-slate-800"><span className="font-medium">{p.agreement.requested_by_name ?? 'Your manager'}:</span> {p.agreement.message}</p>}
          <div className="max-h-[40vh] overflow-y-auto whitespace-pre-wrap rounded-md border border-slate-200 p-4 text-sm leading-relaxed dark:border-slate-700" data-testid="agreement-text" tabIndex={0}>{p.text}</div>
          <p className="text-xs text-slate-500">Signing is your choice and you can’t be treated unfairly for saying no. You can withdraw later with {noticeLabel(p.agreement.notice_days ?? 7)} notice.</p>
          <SignatureForm signerName={p.signer_name} busy={busy} submitLabel="Sign agreement" onCancel={() => setDecline(true)}
            onSubmit={async s => {
              setBusy(true);
              try { const r = await api.wf.signAgreement(ws!, id, s); toast('Signed. A copy is saved on your profile.', 'success'); done(r); }
              catch (e) { toast(errorMessage(e), 'error'); } finally { setBusy(false); }
            }} />
        </div>
      )}
      <ConfirmDialog open={decline} title="Decline this request?" confirmLabel="Decline" onCancel={() => { setDecline(false); onClose(); }}
        onConfirm={async () => { try { await api.wf.cancelAgreement(ws!, id); toast('Declined', 'success'); done(); } catch (e) { toast(errorMessage(e), 'error'); } }}>
        You’ll stay on the normal 48-hour average limit. Choose “Cancel” to decide later.
      </ConfirmDialog>
    </Modal>
  );
}

export function WithdrawModal({ status, signerName, onClose }: { status: WtrStatus; signerName: string; onClose: () => void }) {
  const ws = useWorkspaceId();
  const toast = useUi(s => s.toast);
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const ends = DateTime.fromISO(londonToday()).plus({ days: status.notice_days }).toISODate();
  return (
    <Modal open onClose={onClose} title="Withdraw your 48-hour opt-out">
      <div className="space-y-4">
        <p className="text-sm">You agreed to give {noticeLabel(status.notice_days)} notice. If you sign today, the opt-out ends on <span className="font-medium" data-testid="withdraw-ends">{fmt.date(ends)}</span> and from then your working week is limited to an average of 48 hours.</p>
        <SignatureForm signerName={signerName} busy={busy} submitLabel="Sign withdrawal" onCancel={onClose}
          onSubmit={async s => {
            setBusy(true);
            try { await api.wf.withdrawWtr(ws!, s); toast('Withdrawal signed', 'success'); void qc.invalidateQueries({ queryKey: ['wf', ws] }); onClose(); }
            catch (e) { toast(errorMessage(e), 'error'); } finally { setBusy(false); }
          }} />
      </div>
    </Modal>
  );
}

function RequestModal({ userId, name, legacy, onClose }: { userId: string; name: string; legacy: boolean; onClose: () => void }) {
  const ws = useWorkspaceId();
  const toast = useUi(s => s.toast);
  const qc = useQueryClient();
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  return (
    <Modal open onClose={onClose} title="Request a signature"
      footer={<><Button variant="ghost" onClick={onClose}>Cancel</Button><Button loading={busy} data-testid="request-send" onClick={async () => {
        setBusy(true);
        try { await api.wf.requestWtr(ws!, userId, msg || undefined); toast(`Sent to ${name}`, 'success'); void qc.invalidateQueries({ queryKey: ['wf', ws] }); onClose(); }
        catch (e) { toast(errorMessage(e), 'error'); } finally { setBusy(false); }
      }}>Send request</Button></>}>
      <p className="mb-3 text-sm">{name} gets a notification to read and sign the 48-hour opt-out agreement. {legacy ? 'Their current opt-out stays in place until they sign or withdraw.' : 'Nothing changes unless they sign.'} You can’t opt someone out on their behalf.</p>
      <Field label="Message (optional)"><Textarea rows={3} value={msg} onChange={e => setMsg(e.target.value)} placeholder="e.g. Only if you want the extra overtime" /></Field>
    </Modal>
  );
}

/** Status + actions + documents + history: used on the person's profile (Documents tab) and in Account > Agreements. */
export function WtrPanel({ userId, name, self, canManage, openSign }: { userId: string; name: string; self: boolean; canManage: boolean; openSign?: string | null }) {
  const ws = useWorkspaceId();
  const q = useQuery({ queryKey: ['wf', ws, 'wtr', userId], queryFn: () => (self ? api.wf.myWtr(ws!) : api.wf.employeeWtr(ws!, userId)), enabled: Boolean(ws) });
  const hist = useQuery({ queryKey: ['wf', ws, 'employee-history', userId, 'wtr'], queryFn: () => api.wf.employeeHistory(ws!, userId, ['workforce.wtr.']), enabled: Boolean(ws) });
  const [modal, setModal] = useState<{ kind: 'sign'; id: string } | { kind: 'withdraw' } | { kind: 'request' } | { kind: 'view'; a: Agreement } | null>(null);
  useEffect(() => { if (openSign && self) setModal({ kind: 'sign', id: openSign }); }, [openSign, self]);
  const toast = useUi(x => x.toast);
  // An employee opting out of their own accord: create the request, then go straight to signing.
  const selfStart = async () => {
    try { const a = await api.wf.requestWtr(ws!, userId); void q.refetch(); setModal({ kind: 'sign', id: a.id }); } catch (e) { toast(errorMessage(e), 'error'); }
  };
  const s = q.data;
  if (!s) return q.error ? <p role="alert">{errorMessage(q.error)}</p> : <Spinner />;
  const signed = s.agreements.filter(a => a.status === 'signed');
  return (
    <section className="space-y-4" data-testid="wtr-panel">
      <div className="flex flex-wrap items-center gap-3">
        <h3 className="text-sm font-semibold">48-hour week opt-out</h3>
        <WtrBadge state={s.state} />
        <div className="ml-auto flex gap-2">
          {s.pending && self && <Button size="sm" onClick={() => setModal({ kind: 'sign', id: s.pending!.id })} data-testid="wtr-sign">Read and sign</Button>}
          {!s.pending && (canManage || self) && ['none', 'legacy', 'withdrawn'].includes(s.state) && (
            <Button size="sm" variant={self ? 'secondary' : 'primary'} onClick={() => void (self ? selfStart() : setModal({ kind: 'request' }))} data-testid="wtr-request">
              {self ? (s.state === 'legacy' ? 'Sign a new agreement' : 'Opt out (sign agreement)') : 'Request signature'}
            </Button>)}
          {self && ['opted_out', 'legacy'].includes(s.state) && <Button size="sm" variant="ghost" onClick={() => setModal({ kind: 'withdraw' })} data-testid="wtr-withdraw">Withdraw…</Button>}
        </div>
      </div>
      <p className="text-sm text-slate-600 dark:text-slate-300" data-testid="wtr-explain">
        {s.state === 'legacy' ? 'Recorded as opted out before signed agreements existed: there’s no signed copy on file. It still counts, but a signature is required.'
          : s.state === 'opted_out' ? `Signed ${fmt.date(s.signed_on)}. Withdrawing needs ${noticeLabel(s.notice_days)} notice.`
          : s.state === 'withdrawing' ? `Withdrawn: the opt-out ends ${fmt.date(s.ends_on)}. Until then the 48-hour average limit doesn’t apply.`
          : s.state === 'withdrawn' ? `Withdrawn on ${fmt.date(s.ends_on)}. The 48-hour average limit applies.`
          : 'Not opted out: the 48-hour average limit applies.'}
        {s.pending && ` A signature request from ${s.pending.requested_by_name ?? 'a manager'} is waiting (sent ${fmt.date(s.pending.requested_at)}).`}
      </p>
      {s.pending && !self && canManage && <Button size="sm" variant="ghost" onClick={async () => { await api.wf.cancelAgreement(ws!, s.pending!.id); void q.refetch(); void hist.refetch(); }}>Cancel request</Button>}
      <div>
        <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">Signed documents</h4>
        {signed.length === 0 ? <p className="text-sm text-slate-500">None yet.</p> : (
          <ul className="divide-y divide-slate-100 rounded-md border border-slate-200 text-sm dark:divide-slate-800 dark:border-slate-700" data-testid="wtr-documents">
            {signed.map(a => (
              <li key={a.id} className="flex items-center gap-3 px-3 py-2">
                <FileSignature className="h-4 w-4 text-slate-500" aria-hidden />
                <span className="flex-1">{AGREEMENT_LABEL[a.kind]} · signed {fmt.date(a.signed_on)} by {a.signed_name}{a.kind === 'wtr_withdrawal' && a.effective_on ? ` · ends ${fmt.date(a.effective_on)}` : ''}</span>
                <Button size="sm" variant="ghost" onClick={() => setModal({ kind: 'view', a })}>View</Button>
                <Button size="sm" variant="ghost" aria-label={`Download ${AGREEMENT_LABEL[a.kind]}`} onClick={() => void download(ws!, a)}><Download className="h-4 w-4" /></Button>
              </li>))}
          </ul>)}
      </div>
      <div>
        <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">History</h4>
        {hist.isLoading ? <Spinner /> : <HistoryList entries={hist.data?.entries ?? []} describe={describeWtrEvent} />}
      </div>
      {modal?.kind === 'sign' && <SignAgreementModal id={modal.id} onClose={() => { setModal(null); void hist.refetch(); }} />}
      {modal?.kind === 'withdraw' && <WithdrawModal status={s} signerName={name} onClose={() => { setModal(null); void hist.refetch(); }} />}
      {modal?.kind === 'view' && <SignedCopyModal agreement={modal.a} onClose={() => setModal(null)} />}
      {modal?.kind === 'request' && <RequestModal userId={userId} name={name} legacy={s.state === 'legacy'} onClose={() => setModal(null)} />}
    </section>
  );
}

/** Account > Agreements & forms: the employee's own view (notification links open the waiting request with ?sign=). */
export function MyAgreementsPanel({ sign }: { sign?: string | null }) {
  const { userId } = useAuth();
  const me = useMe();
  if (!userId || !me.data) return <Spinner />;
  return <Card><WtrPanel userId={userId} name={me.data.profile.display_name} self canManage={false} openSign={sign} /></Card>;
}
